import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  AlertTriangle, CheckCircle, XCircle, Trash2, Ban, Eye, 
  Shield, TrendingUp, Users, FileText 
} from 'lucide-react';
import { formatTimeAgo } from '../../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminModerationPage() {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedReport, setSelectedReport] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadReports();
    loadStats();
  }, [filter]);

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/moderation/stats`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      const statusParam = filter === 'all' ? '' : `?status=${filter}`;
      const res = await fetch(`${API_URL}/api/admin/reports${statusParam}`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setReports(data.reports || []);
      }
    } catch (error) {
      console.error('Failed to load reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewAction = async (action) => {
    if (!selectedReport) return;

    try {
      setActionLoading(true);
      const res = await fetch(
        `${API_URL}/api/admin/reports/${selectedReport.id}/review`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            action,
            notes: reviewNotes
          })
        }
      );

      if (res.ok) {
        toast.success(`Action: ${action} completed`);
        setSelectedReport(null);
        setReviewNotes('');
        loadReports();
        loadStats();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Action failed');
      }
    } catch (error) {
      toast.error('Failed to complete action');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemovePost = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/posts/${postId}/remove`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success('Post removed');
        loadReports();
      } else {
        toast.error('Failed to remove post');
      }
    } catch (error) {
      toast.error('Failed to remove post');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      spam: 'bg-yellow-100 text-yellow-800',
      scam: 'bg-red-100 text-red-800',
      inappropriate: 'bg-orange-100 text-orange-800',
      misleading: 'bg-blue-100 text-blue-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.other;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Content Moderation</h1>
            <p className="text-gray-600">Review and manage reported content</p>
          </div>
          <Shield className="h-12 w-12 text-blue-500" />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Pending Reports</CardTitle>
              <AlertTriangle className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pending_reports || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Reports</CardTitle>
              <FileText className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_reports || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Hidden Posts</CardTitle>
              <Eye className="h-4 w-4 text-gray-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.hidden_posts || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Suspended Users</CardTitle>
              <Ban className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.suspended_users || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Reports Table */}
        <Card>
          <CardHeader>
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList>
                <TabsTrigger value="pending">Pending</TabsTrigger>
                <TabsTrigger value="reviewed">Reviewed</TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading reports...</div>
            ) : reports.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No {filter} reports found
              </div>
            ) : (
              <div className="space-y-4">
                {reports.map((report) => (
                  <Card key={report.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getCategoryColor(report.category)}>
                            {report.category}
                          </Badge>
                          <Badge variant="outline">{report.status}</Badge>
                          <span className="text-sm text-gray-500">
                            {formatTimeAgo(report.created_at)}
                          </span>
                        </div>

                        <p className="font-medium">Reason: {report.reason}</p>
                        {report.details && (
                          <p className="text-sm text-gray-600 mt-1">{report.details}</p>
                        )}

                        <div className="mt-2 text-sm text-gray-500">
                          Reported by: {report.reporter_username || 'Anonymous'}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedReport(report)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Review
                        </Button>
                        {report.post_id && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemovePost(report.post_id)}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Remove Post
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Review Dialog */}
      <Dialog open={!!selectedReport} onOpenChange={() => setSelectedReport(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review Report</DialogTitle>
          </DialogHeader>

          {selectedReport && (
            <div className="space-y-4">
              <div>
                <p className="font-medium">Category: {selectedReport.category}</p>
                <p className="text-sm text-gray-600">Reason: {selectedReport.reason}</p>
                {selectedReport.details && (
                  <p className="text-sm text-gray-600 mt-2">{selectedReport.details}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Admin Notes</label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes about your decision..."
                  rows={3}
                />
              </div>

              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => handleReviewAction('approve')}
                  disabled={actionLoading}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve (No Action)
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleReviewAction('reject')}
                  disabled={actionLoading}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject Report
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleReviewAction('remove_post')}
                  disabled={actionLoading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove Post
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleReviewAction('suspend_user')}
                  disabled={actionLoading}
                >
                  <Ban className="h-4 w-4 mr-2" />
                  Suspend User
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedReport(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
